/**
 * Global paywall bus (web) — twin of src/app/lib/paywall.ts.
 *
 * Thin pub/sub that lets ANYTHING in the app request the canonical
 * PaywallSheet to open without threading open-state through props.
 *
 * Two callers in practice:
 *   1. Explicit `requestPaywall()` from a UI handler (DM inbox empty
 *      state, Home pitch, public-profile DM gate,
 *      Settings manage button).
 *   2. The convex client interceptor in `convexDispatch.ts`, which
 *      fires automatically on any premium-shaped ConvexError
 *      (PREMIUM_REQUIRED / BOOKMARK_LIMIT_REACHED /
 *      DM_THREAD_LIMIT_REACHED). Safety net for cases where the
 *      pre-check didn't run or races a stale reactive query.
 *
 * A single `<PaywallHost />` mounted in `main.tsx` subscribes to the
 * bus and renders the sheet.
 *
 * No React deps in this module so it can be imported from
 * convexDispatch.ts (which lives outside the component tree).
 */
import type { PaywallTrigger } from '@/components/PaywallSheet';

export type { PaywallTrigger };

/** Per-target binding for one-time purchases (mobile-only at purchase
 *  time; web just carries it for call-site parity). */
export interface PaywallBindTarget {
  type: 'dmThread' | 'rating';
  id: string;
}

/** One-time consumable offer hint. On web there's no IAP, so the sheet
 *  shows the price but routes the button to Get-the-app. Mirror of
 *  src/app/lib/paywall.ts so call sites are identical across surfaces. */
export interface PaywallOneTime {
  action: string;
  bindTarget?: PaywallBindTarget;
}

export interface PaywallEvent {
  trigger: PaywallTrigger;
  oneTime?: PaywallOneTime;
}

type Listener = (event: PaywallEvent) => void;

const listeners = new Set<Listener>();

/**
 * Open the global PaywallSheet. Idempotent — if the sheet is
 * already open, the subscriber simply updates the trigger so
 * the copy reflects the latest action the user tried.
 */
export function requestPaywall(event: PaywallEvent) {
  for (const fn of listeners) {
    try { fn(event); } catch { /* listener errors are non-fatal */ }
  }
}

/**
 * Subscribe to paywall-open requests. Returns an unsubscribe fn —
 * call in a useEffect cleanup.
 */
export function subscribeToPaywall(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
